// Form integration management for CTM Chat Widget
export class FormManager {
  constructor() {
    this.formScriptOrigins = new Map();
    this.currentFormConfig = null;
    this.messageHandler = null;

    // Callbacks
    this.onFormSubmit = null;
    this.onFormResize = null;
    this.onSendMessage = null;
    this.onDispatchEvent = null;

    // Bind methods
    this.handleWindowMessage = this.handleWindowMessage.bind(this);
  }

  // Initialize the form manager with dependencies
  initialize(options = {}) {
    this.messageHandler = options.messageHandler;
    this.onFormSubmit = options.onFormSubmit || (() => {});
    this.onFormResize = options.onFormResize || (() => {});
    this.onSendMessage = options.onSendMessage || (() => {});
    this.onDispatchEvent = options.onDispatchEvent || (() => {});

    // Setup window message listener for postMessage communication
    window.addEventListener("message", this.handleWindowMessage);
  }

  // Clean up resources
  cleanup() {
    window.removeEventListener("message", this.handleWindowMessage);
    this.formScriptOrigins.clear();
    this.currentFormConfig = null;
  }

  // Register a form with its origin data
  registerForm(formId, data) {
    this.formScriptOrigins.set(formId, data);
    this.sendVisitorSessionToForm(formId, data);
  }

  /**
   * Sends the CTM visitor session directly to a FormReactor iframe.
   *
   * @param {string} formId FormReactor's public form identifier.
   * @param {object} formData Registered iframe and visitor data.
   * @param {boolean} retryOnLoad Whether to send again after iframe load.
   */
  sendVisitorSessionToForm(formId, formData, retryOnLoad = true) {
    const visitorSid = formData?.visitorSid;
    const iframe = formData?.div?.querySelector?.("iframe");
    if (
      !iframe ||
      typeof visitorSid !== "string" ||
      !/^[0-9a-f]{24}$/i.test(visitorSid) ||
      !formData.origin
    ) {
      return;
    }

    const sessionMessage = {
      action: "session",
      sid: visitorSid,
      parentPage: window.location.toString(),
      parentReferrer: document.referrer,
      frame_id: iframe.id || formId,
    };
    const sendSession = () => {
      iframe.contentWindow?.postMessage(sessionMessage, formData.origin);
    };

    // The immediate attempt covers an iframe whose listener is already ready;
    // the load attempt covers a newly rendered cross-origin FormReactor.
    if (retryOnLoad) {
      iframe.addEventListener("load", sendSession, { once: true });
    }
    sendSession();
  }

  // Handle window postMessage events from forms
  handleWindowMessage(event) {
    if (!this.formScriptOrigins.has(event.data?.id)) {
      return;
    }

    const formData = this.formScriptOrigins.get(event.data.id);

    switch (event.data?.action) {
      case "startup":
        // FormReactor emits startup after its postMessage listener is ready.
        this.sendVisitorSessionToForm(event.data.id, formData, false);
        break;
      case "resize":
        this.handleFormResize(formData, event.data.height);
        break;
      case "success":
        this.handleFormSubmission(event.data.id, event.data.formData);
        break;
    }
  }

  // Handle form resize events
  handleFormResize(formData, height) {
    const iframe = formData.div.querySelector("iframe");
    if (iframe) {
      iframe.style.height = height + "px";
    }

    // Notify parent of resize
    if (this.onFormResize) {
      this.onFormResize();
    }
  }

  // Handle form submission
  handleFormSubmission(formId, submittedData) {
    const formConfig = this.formScriptOrigins.get(formId);

    // Dispatch form submission event
    if (this.onDispatchEvent) {
      this.onDispatchEvent("action", {
        actionType: "formSubmission",
        formId,
        submittedData,
        timestamp: Date.now(),
      });
    }

    // Notify parent about form submission
    if (this.onFormSubmit) {
      this.onFormSubmit({ formId, submittedData, formConfig });
    }

    // Check if we should send to AI
    if (formConfig.send_to_ai) {
      this.handleFormSubmissionWithAI(formConfig, submittedData);
    } else {
      this.handleFormSubmissionWithoutAI(formConfig, submittedData);
    }
  }

  // Handle form submission that should be sent to AI
  handleFormSubmissionWithAI(formConfig, submittedData) {
    const startMessage = {
      type: "user",
      content: `Form submitted: ${JSON.stringify(submittedData)}`,
      timestamp: Date.now(),
      isFormSubmission: true,
      formData: submittedData,
    };

    // Send appropriate message based on AI state
    if (this.onSendMessage) {
      const aiEnabled = formConfig.aiEnabled || false;

      if (!aiEnabled) {
        // Activate AI with form data
        this.onSendMessage({
          type: "activateAI",
          content: startMessage.content,
          formData: submittedData,
        });
      } else {
        // Send form data to AI
        this.onSendMessage(startMessage);
      }
    }

    // Show processing status
    if (this.messageHandler) {
      this.messageHandler.addMessage({
        type: "status",
        content:
          "Thank you for submitting the form. Processing your request...",
      });
    }
  }

  // Handle form submission that doesn't go to AI
  handleFormSubmissionWithoutAI(formConfig) {
    // Show completion message
    if (this.messageHandler) {
      this.messageHandler.addMessage({
        type: "status",
        content:
          "Thank you for submitting the form. Your information has been received.",
      });
    }

    // previously we rendered a green check mark here along with a success message
    // it was removed so the form reactor completion message (markdown) is visible instead
  }

  // Store current form configuration
  setCurrentFormConfig(config) {
    this.currentFormConfig = config;
  }

  // Get current form configuration
  getCurrentFormConfig() {
    return this.currentFormConfig;
  }

  // Check if a form script has already been loaded
  isFormScriptLoaded() {
    return window.__ctm_loader_run;
  }

  // Create form message object for display
  createFormMessage(payload) {
    return {
      type: "form",
      form: payload.form,
      send_to_ai: payload.send_to_ai || false,
      formConfig: payload,
    };
  }

  // Extract form ID from URL
  extractFormId(url) {
    return url.split("/").pop().split(".").shift();
  }

  // Get form origin from URL
  getFormOrigin(url) {
    return new URL(url).origin;
  }
}
